import { SxProps, Theme, styled } from '@mui/material/styles';
import React, { ForwardedRef, forwardRef } from 'react';
import { Link as RawLink, LinkProps } from 'react-router-dom';
import Typography from '@mui/material/Typography';

// Legacy look (primary / secondary) — kept untouched so existing consumers are
// not impacted (default anchor underline, original icon offset).
const Link = styled(RawLink)<LinkProps>(({ theme, color }) => ({
  marginRight: '0.1em',
  display: 'flex',
  alignItems: 'center',
  color: color === 'primary' ? theme.palette.red[60] : theme.palette.blue[60],
}));

const StyledIcon = styled('span')(
  () => `
  margin-right: 0.70em;
  position: relative;
  top: 0.15em;
  `,
);

// Clean look (tertiary) — no underline, medium-weight text, icon vertically
// centered with the text and sharing its colour through `currentColor`.
const CleanLink = styled(RawLink)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.5em',
  color: theme.palette.blue[60],
  fontFamily: '"Poppins Medium", sans-serif',
  fontWeight: 600,
  fontSize: 12,
  lineHeight: 1.2,
  textDecoration: 'none',
  '&:hover': { textDecoration: 'none', opacity: 0.8 },
}));

const IconWrap = styled('span')(() => ({
  display: 'inline-flex',
  alignItems: 'center',
  lineHeight: 0,
}));

export type HrefLinkColor = 'primary' | 'secondary' | 'tertiary';

export type HrefLinkProps = {
  icon?: React.ReactNode;
  /** Side the icon is rendered on (tertiary variant). Defaults to `left`. */
  iconPosition?: 'left' | 'right';
  children?: React.ReactNode;
  text?: string;
  classname?: string;
  href: string;
  id?: string;
  title?: string;
  sx?: SxProps<Theme>;
  /** `tertiary` gives the clean, underline-less link look. */
  color?: HrefLinkColor;
  target?: '_blank' | '_self' | '_parent' | '_top' | string;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
};

export const HrefLink = forwardRef(
  (
    {
      text,
      href,
      icon,
      iconPosition = 'left',
      id,
      title,
      children,
      sx,
      target = '_blank',
      color = 'primary',
      onClick,
    }: HrefLinkProps,
    ref: ForwardedRef<HTMLAnchorElement>,
  ): React.ReactElement => {
    if (color === 'tertiary') {
      const iconNode = icon ? <IconWrap>{icon}</IconWrap> : null;
      return (
        <CleanLink
          onClick={onClick}
          id={id}
          to={href}
          title={title || ''}
          target={target}
          rel="noreferrer"
          sx={sx}
          ref={ref}
        >
          {iconPosition === 'left' ? iconNode : null}
          <span>{text || children}</span>
          {iconPosition === 'right' ? iconNode : null}
        </CleanLink>
      );
    }

    return (
      <Link
        onClick={onClick}
        id={id}
        to={href}
        title={title || ''}
        target={target}
        rel="noreferrer"
        sx={sx}
        ref={ref}
        color={color}
      >
        <Typography variant="body1Light" color={color}>
          {icon && <StyledIcon>{icon}</StyledIcon>}
          {text || children}
        </Typography>
      </Link>
    );
  },
);
